import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Trash2, Key, Copy, Check, ExternalLink, AlertTriangle,
  Shield, Clock, Activity, BarChart3, RefreshCw, ChevronLeft, ChevronRight,
  Zap, AlertCircle, TrendingUp, Filter,
} from "lucide-react";

// ============ API DOCS DATA ============

const API_ENDPOINTS = [
  {
    method: "GET", path: "/api/v1/business", desc: "Info attività",
    params: null,
    response: `{ "business_id": "biz_xxx", "name": "Salone ABC", "slug": "salone-abc", "address": "Via Roma 1", "phone": "+39...", "email": "info@..." }`,
    notes: "Restituisce solo campi pubblici, mai dati sensibili."
  },
  {
    method: "GET", path: "/api/v1/services", desc: "Lista servizi attivi",
    params: null,
    response: `{ "services": [{ "service_id": "srv_xxx", "name": "Taglio", "price": 25, "duration_minutes": 30 }], "count": 5 }`,
    notes: "Solo servizi attivi, ordinati per posizione."
  },
  {
    method: "GET", path: "/api/v1/categories", desc: "Lista categorie",
    params: null,
    response: `{ "categories": [{ "category_id": "cat_xxx", "name": "Capelli", "order": 0 }], "count": 3 }`,
    notes: null
  },
  {
    method: "GET", path: "/api/v1/staff", desc: "Lista staff",
    params: null,
    response: `{ "staff": [{ "staff_id": "stf_xxx", "name": "Mario", "role": "operator" }], "count": 2 }`,
    notes: "Password e dati sensibili mai esposti."
  },
  {
    method: "GET", path: "/api/v1/availability", desc: "Slot disponibili per data",
    params: [
      { name: "date", type: "string", required: true, desc: "Data formato YYYY-MM-DD" },
      { name: "service_id", type: "string", required: false, desc: "Filtra per servizio" },
      { name: "staff_id", type: "string", required: false, desc: "Filtra per staff" },
    ],
    response: `{ "slots": [{ "time": "09:00", "available": true }, { "time": "09:30", "available": false }], "date": "2026-03-15" }`,
    notes: "Se il giorno è chiuso, restituisce array vuoto con messaggio."
  },
  {
    method: "GET", path: "/api/v1/bookings", desc: "Lista prenotazioni",
    params: [
      { name: "date_from", type: "string", required: false, desc: "Data inizio (YYYY-MM-DD)" },
      { name: "date_to", type: "string", required: false, desc: "Data fine (YYYY-MM-DD)" },
      { name: "status", type: "string", required: false, desc: "confirmed | cancelled | pending" },
      { name: "limit", type: "int", required: false, desc: "Max risultati (1-100, default 50)" },
      { name: "offset", type: "int", required: false, desc: "Offset paginazione (default 0)" },
    ],
    response: `{ "bookings": [{ "booking_id": "bk_xxx", "customer_name": "Mario Rossi", "status": "confirmed" }], "total": 120 }`,
    notes: "Ordinate per data decrescente. Il campo manage_token non viene mai esposto."
  },
  {
    method: "GET", path: "/api/v1/bookings/{booking_id}", desc: "Dettaglio singola prenotazione",
    params: [{ name: "booking_id", type: "string", required: true, desc: "ID prenotazione (path)" }],
    response: `{ "booking_id": "bk_xxx", "customer_name": "...", "services_details": [...], "total_price": 45 }`,
    notes: null
  },
  {
    method: "POST", path: "/api/v1/bookings", desc: "Crea prenotazione",
    params: [
      { name: "customer_name", type: "string", required: true, desc: "Nome del cliente" },
      { name: "service_ids", type: "string[]", required: true, desc: "Array di ID servizi" },
      { name: "start_datetime", type: "string", required: true, desc: "Data/ora inizio ISO 8601" },
      { name: "staff_id", type: "string", required: false, desc: "ID membro staff" },
      { name: "customer_email", type: "string", required: false, desc: "Email cliente" },
      { name: "customer_phone", type: "string", required: false, desc: "Telefono cliente" },
      { name: "discount_code", type: "string", required: false, desc: "Codice sconto" },
      { name: "send_email", type: "boolean", required: false, desc: "Invia email conferma" },
      { name: "notes", type: "string", required: false, desc: "Note aggiuntive" },
    ],
    response: `{ "booking_id": "bk_xxx", "status": "confirmed", "total_price": 45 }`,
    notes: "Sconto validato completamente (date, max utilizzi, importo minimo)."
  },
  {
    method: "PUT", path: "/api/v1/bookings/{booking_id}/cancel", desc: "Cancella prenotazione",
    params: [{ name: "booking_id", type: "string", required: true, desc: "ID prenotazione (path)" }],
    response: `{ "message": "Booking cancelled", "booking_id": "bk_xxx" }`,
    notes: "404 se già cancellata o non trovata."
  },
  {
    method: "GET", path: "/api/v1/customers", desc: "Lista clienti",
    params: [
      { name: "limit", type: "int", required: false, desc: "Max risultati (1-100, default 50)" },
      { name: "offset", type: "int", required: false, desc: "Offset paginazione (default 0)" },
    ],
    response: `{ "customers": [{ "customer_id": "cst_xxx", "name": "Mario Rossi" }], "total": 30 }`,
    notes: null
  },
  {
    method: "POST", path: "/api/v1/validate-discount", desc: "Valida codice sconto",
    params: [
      { name: "code", type: "string", required: true, desc: "Codice sconto" },
      { name: "booking_amount", type: "number", required: false, desc: "Importo per calcolo" },
    ],
    response: `{ "valid": true, "code": "ESTATE20", "discount_amount": 9.00, "final_amount": 36.00 }`,
    notes: "Verifica validità date, limite utilizzi e importo minimo."
  },
];

const ERROR_CODES = [
  { code: 400, desc: "Bad Request", detail: "Parametri mancanti o non validi" },
  { code: 401, desc: "Unauthorized", detail: "API Key mancante, non valida o disattivata" },
  { code: 404, desc: "Not Found", detail: "Risorsa non trovata" },
  { code: 429, desc: "Too Many Requests", detail: "Rate limit superato (max 60 req/min per chiave)" },
  { code: 500, desc: "Internal Server Error", detail: "Errore interno del server" },
];

const methodColors = {
  GET: "bg-green-100 text-green-700 border-green-200",
  POST: "bg-blue-100 text-blue-700 border-blue-200",
  PUT: "bg-amber-100 text-amber-700 border-amber-200",
  DELETE: "bg-red-100 text-red-700 border-red-200",
};

const statusColors = (code) => {
  if (code >= 500) return "bg-red-100 text-red-700";
  if (code === 429) return "bg-orange-100 text-orange-700";
  if (code >= 400) return "bg-amber-100 text-amber-700";
  return "bg-green-100 text-green-700";
};

// ============ MAIN COMPONENT ============

export default function ApiKeysManager({ apiBasePath }) {
  const [keys, setKeys] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("API Key");
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("keys");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API}${apiBasePath}/api-keys`, { headers });
      setKeys(res.data);
    } catch { /* empty */ }
  };

  useEffect(() => { fetchKeys(); }, [apiBasePath]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}${apiBasePath}/api-keys`, { name: newKeyName }, { headers });
      setNewKeyResult(res.data);
      toast.success("API Key creata!");
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    }
  };

  const handleToggle = async (key) => {
    try {
      await axios.put(`${API}${apiBasePath}/api-keys/${key.key_id}`, { is_active: !key.is_active }, { headers });
      toast.success(key.is_active ? "Key disattivata" : "Key attivata");
      fetchKeys();
    } catch { toast.error("Errore"); }
  };

  const handleDelete = async (keyId) => {
    if (!confirm("Eliminare questa API key?")) return;
    try {
      await axios.delete(`${API}${apiBasePath}/api-keys/${keyId}`, { headers });
      toast.success("API Key eliminata");
      fetchKeys();
    } catch { toast.error("Errore"); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="api-keys-title">
            <Key className="h-5 w-5" /> API Keys & Monitoraggio
          </h3>
          <p className="text-sm text-slate-500">Gestisci chiavi, monitora utilizzo e consulta la documentazione</p>
        </div>
        <Button onClick={() => { setNewKeyName("API Key"); setNewKeyResult(null); setCreateDialogOpen(true); }} data-testid="create-api-key-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuova API Key
        </Button>
      </div>

      {/* Sub-tabs: Keys, Logs, Docs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="w-auto" data-testid="api-sub-tabs">
          <TabsTrigger value="keys" data-testid="api-sub-tab-keys"><Key className="h-4 w-4 mr-1.5" />Chiavi</TabsTrigger>
          <TabsTrigger value="logs" data-testid="api-sub-tab-logs"><Activity className="h-4 w-4 mr-1.5" />Activity Log</TabsTrigger>
          <TabsTrigger value="docs" data-testid="api-sub-tab-docs"><ExternalLink className="h-4 w-4 mr-1.5" />Documentazione</TabsTrigger>
        </TabsList>

        {/* ====== KEYS TAB ====== */}
        <TabsContent value="keys" className="mt-4">
          <KeysTable keys={keys} onToggle={handleToggle} onDelete={handleDelete} />
        </TabsContent>

        {/* ====== LOGS TAB ====== */}
        <TabsContent value="logs" className="mt-4">
          <ApiLogsView apiBasePath={apiBasePath} keys={keys} />
        </TabsContent>

        {/* ====== DOCS TAB ====== */}
        <TabsContent value="docs" className="mt-4">
          <ApiDocsView />
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova API Key</DialogTitle></DialogHeader>
          {!newKeyResult ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome (identificativo)</Label>
                <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="Es: Sito Web, CRM, App Mobile..." data-testid="api-key-name-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
                <Button onClick={handleCreate} data-testid="generate-api-key-btn">Genera Key</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Salva questa chiave adesso!</p>
                  <p className="text-amber-600">Non sarà più visibile dopo aver chiuso questa finestra.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>La tua API Key</Label>
                <div className="flex gap-2">
                  <Input value={newKeyResult.api_key} readOnly className="font-mono text-xs" data-testid="new-api-key-value" />
                  <Button size="icon" variant="outline" onClick={() => copyToClipboard(newKeyResult.api_key)} data-testid="copy-api-key-btn">
                    {copiedKey ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setCreateDialogOpen(false); setNewKeyResult(null); }}>Ho salvato la chiave</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ KEYS TABLE ============

function KeysTable({ keys, onToggle, onDelete }) {
  if (keys.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-slate-400">
        <Key className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>Nessuna API key. Crea la prima per abilitare integrazioni esterne.</p>
      </CardContent></Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Key (preview)</TableHead>
              <TableHead>Richieste</TableHead>
              <TableHead>Ultimo utilizzo</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map(k => (
              <TableRow key={k.key_id} className={!k.is_active ? "opacity-60" : ""} data-testid={`api-key-row-${k.key_id}`}>
                <TableCell className="font-medium">{k.name}</TableCell>
                <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{k.key_preview}</code></TableCell>
                <TableCell>{k.request_count || 0}</TableCell>
                <TableCell className="text-xs text-slate-500">
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "Mai"}
                </TableCell>
                <TableCell><Switch checked={k.is_active} onCheckedChange={() => onToggle(k)} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete(k.key_id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ============ API LOGS VIEW ============

function ApiLogsView({ apiBasePath, keys }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filterEndpoint, setFilterEndpoint] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterKey, setFilterKey] = useState("all");
  const [statsDays, setStatsDays] = useState(7);
  const limit = 20;

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(newOffset) });
      if (filterEndpoint) params.set("endpoint", filterEndpoint);
      if (filterStatus !== "all") params.set("status_code", filterStatus);
      if (filterKey !== "all") params.set("key_id", filterKey);
      const res = await axios.get(`${API}${apiBasePath}/api-logs?${params}`, { headers });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setOffset(newOffset);
    } catch { /* empty */ }
    setLoading(false);
  }, [apiBasePath, filterEndpoint, filterStatus, filterKey]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}${apiBasePath}/api-logs/stats?days=${statsDays}`, { headers });
      setStats(res.data);
    } catch { /* empty */ }
  }, [apiBasePath, statsDays]);

  useEffect(() => { fetchLogs(0); fetchStats(); }, [fetchLogs, fetchStats]);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="stat-total-requests">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50"><Zap className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.total_requests}</p>
                <p className="text-xs text-slate-500">Richieste ({stats.period_days}gg)</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-error-rate">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-50"><AlertCircle className="h-5 w-5 text-red-500" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.error_rate}%</p>
                <p className="text-xs text-slate-500">Errori ({stats.error_requests})</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-avg-time">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50"><Clock className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.avg_response_time_ms}ms</p>
                <p className="text-xs text-slate-500">Tempo medio</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-top-endpoint">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50"><TrendingUp className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-sm font-bold truncate max-w-[120px]">{stats.top_endpoints?.[0]?.endpoint || "-"}</p>
                <p className="text-xs text-slate-500">Endpoint top ({stats.top_endpoints?.[0]?.count || 0})</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats period selector + Daily chart */}
      {stats && stats.daily_stats?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold flex items-center gap-1.5"><BarChart3 className="h-4 w-4" /> Richieste giornaliere</h4>
              <Select value={String(statsDays)} onValueChange={(v) => setStatsDays(Number(v))}>
                <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 giorni</SelectItem>
                  <SelectItem value="14">14 giorni</SelectItem>
                  <SelectItem value="30">30 giorni</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DailyChart data={stats.daily_stats} />
            {/* Status distribution */}
            {stats.status_distribution?.length > 0 && (
              <div className="flex gap-3 mt-3 flex-wrap">
                {stats.status_distribution.map(s => (
                  <Badge key={s.status_code} className={`${statusColors(s.status_code)} text-xs font-mono`}>
                    {s.status_code}: {s.count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="h-4 w-4 text-slate-400" />
            <Input placeholder="Filtra endpoint..." value={filterEndpoint} onChange={(e) => setFilterEndpoint(e.target.value)} className="w-44 h-8 text-xs" data-testid="log-filter-endpoint" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="log-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="200">200 OK</SelectItem>
                <SelectItem value="400">400 Bad</SelectItem>
                <SelectItem value="401">401 Auth</SelectItem>
                <SelectItem value="404">404 Not Found</SelectItem>
                <SelectItem value="429">429 Rate</SelectItem>
                <SelectItem value="500">500 Server</SelectItem>
              </SelectContent>
            </Select>
            {keys.length > 0 && (
              <Select value={filterKey} onValueChange={setFilterKey}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="log-filter-key"><SelectValue placeholder="API Key" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le chiavi</SelectItem>
                  {keys.map(k => <SelectItem key={k.key_id} value={k.key_id}>{k.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="sm" className="h-8" onClick={() => { fetchLogs(0); fetchStats(); }} data-testid="log-refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <span className="text-xs text-slate-400 ml-auto">{total} risultati</span>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <Activity className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nessun log trovato. Le chiamate API verranno registrate qui.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-36">Timestamp</TableHead>
                  <TableHead className="w-16">Metodo</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead className="w-16">Status</TableHead>
                  <TableHead className="w-20">Tempo</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead className="w-28">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.log_id} className={log.status_code >= 400 ? "bg-red-50/50" : ""} data-testid={`log-row-${log.log_id}`}>
                    <TableCell className="text-xs font-mono text-slate-500">
                      {new Date(log.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </TableCell>
                    <TableCell><Badge className={`${methodColors[log.method]} text-[10px] font-mono px-1.5`}>{log.method}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{log.endpoint}{log.query_string ? <span className="text-slate-400">?{log.query_string}</span> : ""}</TableCell>
                    <TableCell><Badge className={`${statusColors(log.status_code)} font-mono text-xs`}>{log.status_code}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{log.response_time_ms}ms</TableCell>
                    <TableCell className="text-xs truncate max-w-[100px]">{log.key_name || "-"}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-400">{log.ip_address || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Pagina {currentPage} di {totalPages}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => fetchLogs(offset - limit)} data-testid="log-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => fetchLogs(offset + limit)} data-testid="log-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ DAILY CHART (CSS bars) ============

function DailyChart({ data }) {
  const maxCount = Math.max(...data.map(d => d.requests), 1);
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((d, i) => {
        const height = Math.max((d.requests / maxCount) * 100, 2);
        const errorHeight = d.errors > 0 ? Math.max((d.errors / maxCount) * 100, 2) : 0;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative" title={`${d.date}: ${d.requests} req, ${d.errors} err`}>
            <div className="w-full flex flex-col justify-end" style={{ height: "80px" }}>
              {errorHeight > 0 && <div className="w-full bg-red-400 rounded-t-sm" style={{ height: `${errorHeight}%` }} />}
              <div className="w-full bg-blue-400 rounded-t-sm" style={{ height: `${height - errorHeight}%` }} />
            </div>
            {i === 0 || i === data.length - 1 ? (
              <span className="text-[8px] text-slate-400">{d.date.slice(5)}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ============ API DOCS VIEW ============

function ApiDocsView() {
  return (
    <div className="space-y-4" data-testid="api-docs-section">
      {/* Auth & Security */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" /> Documentazione API v1
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border space-y-2">
              <p className="font-medium text-sm flex items-center gap-1.5"><Key className="h-4 w-4" /> Autenticazione</p>
              <p className="text-xs text-slate-600">Ogni richiesta deve includere l&apos;header <code className="bg-slate-200 px-1 rounded">X-API-Key</code></p>
              <code className="block bg-slate-900 text-green-400 p-3 rounded text-xs mt-2">
                {`curl -H "X-API-Key: pk_your_key" ${API}/api/v1/services`}
              </code>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border space-y-2">
              <p className="font-medium text-sm flex items-center gap-1.5"><Clock className="h-4 w-4" /> Rate Limiting & Sicurezza</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
                <li><strong>60 richieste/minuto</strong> per API key</li>
                <li>Chiavi hashate con SHA-256</li>
                <li>Isolamento multi-tenant</li>
                <li>Dati sensibili mai esposti</li>
                <li>Tutte le chiamate loggate con timing</li>
              </ul>
            </div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
            <p className="font-medium text-blue-800 mb-1">Base URL</p>
            <code className="text-xs bg-blue-100 px-2 py-1 rounded">{API}/api/v1</code>
          </div>
        </CardContent>
      </Card>

      {/* Endpoints */}
      <Card>
        <CardContent className="p-6">
          <h4 className="font-semibold text-base mb-4">Endpoints</h4>
          <Accordion type="multiple" className="space-y-2">
            {API_ENDPOINTS.map((ep, i) => (
              <AccordionItem key={i} value={`ep-${i}`} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-3" data-testid={`api-endpoint-${i}`}>
                  <div className="flex items-center gap-3 text-left">
                    <Badge className={`${methodColors[ep.method]} font-mono text-xs border px-2`}>{ep.method}</Badge>
                    <code className="text-sm">{ep.path}</code>
                    <span className="text-xs text-slate-500 hidden sm:inline">{ep.desc}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-3">
                  <p className="text-sm text-slate-700">{ep.desc}</p>
                  {ep.params?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Parametri</p>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead className="text-xs">Nome</TableHead>
                          <TableHead className="text-xs">Tipo</TableHead>
                          <TableHead className="text-xs">Obb.</TableHead>
                          <TableHead className="text-xs">Descrizione</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {ep.params.map((p, j) => (
                            <TableRow key={j}>
                              <TableCell className="font-mono text-xs">{p.name}</TableCell>
                              <TableCell className="text-xs">{p.type}</TableCell>
                              <TableCell>{p.required ? <Badge variant="destructive" className="text-[10px] px-1.5">Si</Badge> : <Badge variant="secondary" className="text-[10px] px-1.5">No</Badge>}</TableCell>
                              <TableCell className="text-xs text-slate-600">{p.desc}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Risposta</p>
                    <pre className="bg-slate-900 text-green-400 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap">{ep.response}</pre>
                  </div>
                  {ep.notes && <p className="text-xs text-slate-500 italic">{ep.notes}</p>}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Error Codes */}
      <Card>
        <CardContent className="p-6">
          <h4 className="font-semibold text-base mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Codici di Errore
          </h4>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-20">Codice</TableHead>
              <TableHead className="w-40">Stato</TableHead>
              <TableHead>Descrizione</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {ERROR_CODES.map((err, i) => (
                <TableRow key={i}>
                  <TableCell><Badge variant={err.code >= 500 ? "destructive" : "secondary"} className="font-mono">{err.code}</Badge></TableCell>
                  <TableCell className="font-medium text-sm">{err.desc}</TableCell>
                  <TableCell className="text-sm text-slate-600">{err.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Full Example */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h4 className="font-semibold text-base">Esempio Completo</h4>
          <pre className="bg-slate-900 text-green-400 p-4 rounded text-xs overflow-x-auto whitespace-pre">{`# 1. Ottieni servizi
curl -H "X-API-Key: pk_your_key" ${API}/api/v1/services

# 2. Verifica disponibilità
curl -H "X-API-Key: pk_your_key" \\
  "${API}/api/v1/availability?date=2026-06-15&service_id=srv_xxx"

# 3. Crea prenotazione
curl -X POST ${API}/api/v1/bookings \\
  -H "X-API-Key: pk_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "Mario Rossi",
    "customer_email": "mario@email.com",
    "service_ids": ["srv_xxx"],
    "staff_id": "stf_xxx",
    "start_datetime": "2026-06-15T10:00:00",
    "send_email": true
  }'`}</pre>
        </CardContent>
      </Card>
    </div>
  );
}
